import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { makeAutoObservable, reaction } from 'mobx';
import { ReactNode } from 'react';
import { getRootStoreInstance, RootStore } from 'store/rootStore';
import InitiateQuoteFlowDelegate from 'store/flows/initiate-quote/initiateQuoteFlowDelegate';
import { EffectiveDateConfigItem } from 'store/modules/initiate-quote/types';
import {
  DomainProductLineItem,
  DomainProductLinesDelegate,
} from 'store/modules/initiate-quote/domainProductLinesDelegate';
import {
  sunLifeGroupSizeValidation,
  sunLifeSICAdditionalValidations,
  sunLifeSICWarningMessage,
} from 'global/utils/sponsor-validations/sunLife/sicValidation';
import {
  INCORPORATION_DATE_VALIDATION_MSG,
  isIncorporationDateValid,
} from 'global/utils/sponsor-validations/sunLife/incorporationDateValidation';

type SunLifeBasicInfoFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  incorporationDate: FormControl<string>;
  groupSize: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
  sic4: FormControl<string>;
};

export default class BasicInfoStepController {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  private readonly _initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate;
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;
  public form: FormController<SunLifeBasicInfoFields>;

  public get domainProductLineItems(): DomainProductLineItem[] {
    return this.domainProductLinesDelegate.domainProductLineItems;
  }

  public get isDomainProductLinesLoading(): boolean {
    return this.domainProductLinesDelegate.isLoading;
  }

  public get effectiveDatesConfig(): EffectiveDateConfigItem[] {
    return this._initiateQuoteFlowDelegate.effectiveDatesConfig;
  }

  public get isValid(): boolean {
    return (
      this.form.basicFieldsValidationsPassed &&
      !this.form.isLoading &&
      !this.domainProductLinesDelegate.isLoading &&
      this.domainProductLinesDelegate.hasQuotableProductLine
    );
  }

  private get _canFetchProductLines(): boolean {
    const {
      fields: { effectiveDate, zip, state, county, sic4 },
    } = this.form;

    return !!(
      !effectiveDate.unpassedValidationMsg &&
      !zip.unpassedValidationMsg &&
      !state.unpassedValidationMsg &&
      !county.unpassedValidationMsg &&
      !sic4.unpassedValidationMsg
    );
  }

  /** Shown as a warning `Alert` when the current SIC matches a non-blocking rule. */
  public get sicWarningMessage(): ReactNode | null {
    const sicValue = this.form.fields.sic4.value;
    return sicValue && sicValue.length === 4 ? sunLifeSICWarningMessage(sicValue) : null;
  }

  constructor(initiateQuoteFlowDelegate: InitiateQuoteFlowDelegate) {
    this._initiateQuoteFlowDelegate = initiateQuoteFlowDelegate;
    this.form = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>(''),
        groupSize: new FormControl<string>('', {
          customValidations: [sunLifeGroupSizeValidation],
        }),
        incorporationDate: new FormControl<string>('', {
          optional: true,
          enabledWithFields: ['effectiveDate'],
          customValidations: [
            {
              check: (incorporationDate: string): boolean => {
                return isIncorporationDateValid(incorporationDate, this.form.fields.effectiveDate.value);
              },
              msg: INCORPORATION_DATE_VALIDATION_MSG,
            },
          ],
        }),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              zip: { optional: false },
              state: {
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
            },
          },
          {
            type: 'sic',
            params: {
              additionalValidations: sunLifeSICAdditionalValidations,
            },
          },
        ],
      },
    );

    this.domainProductLinesDelegate = this._initiateQuoteFlowDelegate.domainProductLinesDelegate;

    reaction(
      () => [
        this.form.fields.effectiveDate.value,
        this.form.fields.zip.value,
        this.form.fields.state.value,
        this.form.fields.county.value,
        this.form.fields.sic4.value,
      ],
      async () => {
        if (!this._canFetchProductLines) {
          this.domainProductLinesDelegate.clearProductLines();
          return;
        }

        await this.domainProductLinesDelegate.getDomainProductLines(this.form.getFormDataObject(), true);
      },
    );

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
